import './tiptapEditor.css';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import { Button } from 'react-bootstrap';

const TiptapEditor = ({
    value,
    onChange,
    placeholder,
    minHeight = "200px",
    maxHeight = "400px"
}) => {
    const { t } = useTranslation("layout");
    const editor = useEditor({
        extensions: [StarterKit, Underline],
        content: value,
        onUpdate: ({ editor }) => {
            onChange(editor.getHTML());
        },
        editorProps: {
            attributes: {
                class: 'form-control',
                style: `min-height: ${minHeight}; padding: 12px;`,
                placeholder: placeholder ?? t("tiptapEditor.placeholder"),
            },
        },
    });

    if (!editor) {
        return <div className="text-center p-3">{t("tiptapEditor.loading")}</div>;
    }

    return (
        <div className="tiptap-editor border rounded">
            {/* Панель инструментов */}
            <div className="toolbar bg-light border-bottom p-2 d-flex flex-wrap gap-1">
                <Button
                    size="sm"
                    variant={editor.isActive('bold') ? 'primary' : 'outline-secondary'}
                    onClick={() => editor.chain().focus().toggleBold().run()}
                    title={t("tiptapEditor.bold")}
                >
                    <strong>{t("tiptapEditor.boldShort")}</strong>
                </Button>
                <Button
                    size="sm"
                    variant={editor.isActive('italic') ? 'primary' : 'outline-secondary'}
                    onClick={() => editor.chain().focus().toggleItalic().run()}
                    title={t("tiptapEditor.italic")}
                >
                    <em>{t("tiptapEditor.italicShort")}</em>
                </Button>
                <Button
                    size="sm"
                    variant={editor.isActive('underline') ? 'primary' : 'outline-secondary'}
                    onClick={() => editor.chain().focus().toggleUnderline().run()}
                    title={t("tiptapEditor.underline")}
                >
                    <u>{t("tiptapEditor.underlineShort")}</u>
                </Button>
                <div className="vr"></div>
                <Button
                    size="sm"
                    variant={editor.isActive('bulletList') ? 'primary' : 'outline-secondary'}
                    onClick={() => editor.chain().focus().toggleBulletList().run()}
                    title={t("tiptapEditor.bulletList")}
                >
                    • List
                </Button>
                <Button
                    size="sm"
                    variant={editor.isActive('orderedList') ? 'primary' : 'outline-secondary'}
                    onClick={() => editor.chain().focus().toggleOrderedList().run()}
                    title={t("tiptapEditor.orderedList")}
                >
                    1. List
                </Button>
                <div className="vr"></div>
                <Button
                    size="sm"
                    variant={editor.isActive({ heading: { level: 1 } }) ? 'primary' : 'outline-secondary'}
                    onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
                    title={t("tiptapEditor.heading1")}
                >
                    H1
                </Button>
                <Button
                    size="sm"
                    variant={editor.isActive({ heading: { level: 2 } }) ? 'primary' : 'outline-secondary'}
                    onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
                    title={t("tiptapEditor.heading2")}
                >
                    H2
                </Button>
                <Button
                    size="sm"
                    variant={editor.isActive({ heading: { level: 3 } }) ? 'primary' : 'outline-secondary'}
                    onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
                    title={t("tiptapEditor.heading3")}
                >
                    H3
                </Button>
                <div className="vr"></div>
                <Button
                    size="sm"
                    variant="outline-secondary"
                    onClick={() => editor.chain().focus().setParagraph().run()}
                    title={t("tiptapEditor.paragraph")}
                >
                    ¶
                </Button>
                <Button
                    size="sm"
                    variant="outline-danger"
                    onClick={() => editor.chain().focus().clearNodes().unsetAllMarks().run()}
                    title={t("tiptapEditor.clearFormatting")}
                >
                    {t("tiptapEditor.clear")}
                </Button>
            </div>

            {/* Область редактирования */}
            <div style={{ minHeight: minHeight, maxHeight: maxHeight, overflowY: 'auto' }}>
                <EditorContent editor={editor} />
            </div>
        </div>
    );
};

export default TiptapEditor;
